import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  ProductTreePreviewTool,
  ProductPreviewTool,
  SceneDesignerTool,
  LayeredScenePreviewTool,
  RealtimeViewerTool,
} from "@dconfig/react-frontend";
// Dashboard-only author tool — exposed as a package subpath instead of the main
// barrel so it never rides along into the public embed-loader bundle.
import { HookEditorTool } from "@dconfig/react-frontend/tools/HookEditor";
import {
  createTracker,
  listenForViewerMessages,
  observeInteractions,
} from "@dconfig/react-frontend/analytics";
import "@dconfig/react-frontend/styles.css";

// Embed host shell for BaseDashboard's React tools. Lives in the BaseDashboard
// submodule so every deployment gets it for free — the consuming project only
// supplies a wwwroot/embed output path via the ReactFrontend.targets build.
//
// One page, tool selected by URL:
//   /embed/?tool=product-tree-preview&treeId=...&target=...
//   /embed/?tool=product-preview&productId=...&target=...
//   /embed/?tool=scene-designer&target=scene-designer
//   /embed/?tool=layered-scene-preview&id=...
//   /embed/?tool=hook-editor&productId=...
//
// No api-key parameter: published content is anonymous. Unpublished/draft content
// requires the host to be loaded same-origin as a logged-in dashboard session so
// the cookie attaches automatically (the API clients send credentials:"include").
const params = new URLSearchParams(window.location.search);
const tool = params.get("tool") ?? "product-tree-preview";
const treeId = params.get("treeId") ?? "";
const target = params.get("target") ?? undefined;
const stage = (params.get("stage") as "Development" | "Staging" | "Production" | null) ?? undefined;
const baseUrl = params.get("baseUrl") ?? "";
const apiBaseUrl = params.get("apiBaseUrl") ?? undefined;
// Author-only flag the dashboard preview iframe sets. Server ignores it without a cookie.
const includeUnpublished = params.get("includeUnpublished") === "true";

// ── analytics ──────────────────────────────────────────────────────────────
// Public surfaces only. Dashboard-internal tools (hook-editor, the postMessage-
// driven realtime-viewer) and authoring contexts (focus previews, unpublished/
// archived content behind a dashboard session) are never tracked.
const ANALYTICS_SURFACE_BY_TOOL: Record<string, string> = {
  "scene-designer": "designer",
  "product-tree-preview": "product-tree",
  "product-preview": "product-preview",
  "layered-scene-preview": "layered-scene-preview",
};

function initAnalytics(): void {
  const surface = ANALYTICS_SURFACE_BY_TOOL[tool];
  if (!surface) return;
  // Authoring/preview contexts inside the dashboard — skip entirely.
  if (includeUnpublished || params.get("focus") || params.get("allowArchived")) return;
  try {
    const toolId =
      params.get("designerId") ??
      params.get("treeId") ??
      params.get("productId") ??
      params.get("id") ??
      undefined;
    const tracker = createTracker({
      apiBase: apiBaseUrl ?? baseUrl ?? "",
      surface,
      toolId: toolId || undefined,
      market: params.get("market") ?? undefined,
      language: params.get("lang") ?? undefined,
      // URL-level counterpart of the embed attribute, for iframe integrators:
      // /embed/?...&consent=granted|denied|cookieless|banner|cmp:<name>
      consentOverride: params.get("consent"),
      bannerContainer: document.body,
    });
    const track = (type: string, data?: Record<string, unknown>) => tracker.track(type, data);

    // Element heatmap: one delegated listener over the whole SPA.
    observeInteractions(document, track);

    // Viewer bridge events (camera samples, viewer-emitted taxonomy events).
    listenForViewerMessages(track);

    // Position heatmap: a canvas can appear at any time (the 3D viewer mounts
    // lazily), so delegate on pointerdown and check the target instead of
    // observing the DOM for canvas insertions.
    document.addEventListener(
      "pointerdown",
      (e) => {
        try {
          const target = e.target as HTMLElement | null;
          if (!target || target.tagName !== "CANVAS") return;
          const rect = target.getBoundingClientRect();
          if (rect.width <= 0 || rect.height <= 0) return;
          const x = (e.clientX - rect.left) / rect.width;
          const y = (e.clientY - rect.top) / rect.height;
          track("ui.interaction", {
            x: Math.round(x * 1000) / 1000,
            y: Math.round(y * 1000) / 1000,
          });
        } catch {
          /* analytics must never break the tool */
        }
      },
      { capture: true, passive: true },
    );
  } catch {
    /* analytics must never break the tool */
  }
}

initAnalytics();

const root = createRoot(document.getElementById("root")!);

function renderTool() {
  if (tool === "product-tree-preview") {
    if (!treeId) return <ErrorView message="Missing ?treeId parameter." />;
    return (
      <ProductTreePreviewTool
        treeId={treeId}
        baseUrl={baseUrl}
        apiBaseUrl={apiBaseUrl}
        target={target}
        stage={stage}
        includeUnpublished={includeUnpublished}
      />
    );
  }
  if (tool === "product-preview") {
    const productId = params.get("productId") ?? "";
    if (!productId) return <ErrorView message="Missing ?productId parameter." />;
    return (
      <ProductPreviewTool
        productId={productId}
        baseUrl={baseUrl}
        apiBaseUrl={apiBaseUrl}
        target={target}
        stage={stage}
      />
    );
  }
  if (tool === "scene-designer") {
    // With ?designerId=… the tool fetches a configured Designer (catalog scope,
    // layout, theme, pinned Unity build). Without it, falls back to the bare
    // catalog endpoint with the default scene-designer Unity target — useful
    // for the demo URL and local development.
    const designerId = params.get("designerId") ?? undefined;
    // Focus-mode preview parameters (used by the dashboard's slot-mapping
    // editor). When set, SceneDesignerTool short-circuits the full
    // configurator and renders just one slot against a mocked scene.
    const focus = params.get("focus") ?? undefined;
    const mockState = params.get("mockState") ?? undefined;
    // Dashboard preview opt-in for archived Designers. The public embed
    // refuses archived rows; the dashboard's in-app preview iframe sets
    // this so authors can still inspect what they archived.
    const allowArchivedRaw = params.get("allowArchived");
    const allowArchived = allowArchivedRaw === "1" || allowArchivedRaw === "true";
    // Short language code (e.g. "en", "dk"). Resolved to a translation server-side;
    // unknown/blank codes fall back to the primary language.
    const lang = params.get("lang") ?? undefined;
    return (
      <SceneDesignerTool
        baseUrl={baseUrl}
        apiBaseUrl={apiBaseUrl}
        target={target ?? (designerId ? undefined : "scene-designer")}
        stage={stage}
        designerId={designerId}
        allowArchived={allowArchived}
        focus={focus}
        mockState={mockState}
        lang={lang}
      />
    );
  }
  if (tool === "realtime-viewer") {
    // Bare full-window Unity viewer, intended to be loaded inside an iframe.
    // Driven entirely by the parent over postMessage — boots empty, waits for
    // rt:setViewer + rt:setProduct + rt:setMaterials.
    return <RealtimeViewerTool baseUrl={baseUrl} apiBaseUrl={apiBaseUrl} />;
  }
  if (tool === "hook-editor") {
    // Attachment-hook authoring for one product (ATTACHMENT-SYSTEM-DESIGN.md).
    // Dashboard-facing: the /api/attachment endpoints require the dashboard
    // session cookie, so this only works loaded same-origin by a logged-in
    // author (the client sends credentials:"include" like the others).
    const productId = params.get("productId") ?? "";
    if (!productId) return <ErrorView message="Missing ?productId parameter." />;
    const productName = params.get("productName") ?? undefined;
    return (
      <HookEditorTool
        baseUrl={baseUrl}
        productId={productId}
        productName={productName}
      />
    );
  }
  if (tool === "layered-scene-preview") {
    // Accepts ?id= (scene GUID, preferred) or ?slug=.
    const idOrSlug = params.get("id") ?? params.get("slug") ?? "";
    if (!idOrSlug) return <ErrorView message="Missing ?id parameter." />;
    const langParam = params.get("lang");
    return (
      <LayeredScenePreviewTool
        idOrSlug={idOrSlug}
        baseUrl={baseUrl}
        lang={langParam ? Number(langParam) : undefined}
        includeUnpublished={includeUnpublished}
      />
    );
  }
  return <ErrorView message={`Unknown tool: ${tool}`} />;
}

function ErrorView({ message }: { message: string }) {
  return (
    <div style={{ padding: 24, fontFamily: "system-ui", color: "#374151" }}>
      <h1 style={{ fontSize: 18, margin: 0 }}>Embed error</h1>
      <p style={{ marginTop: 8 }}>{message}</p>
      <p style={{ color: "#6b7280", fontSize: 13 }}>
        Expected URL form: <code>/embed/?tool=product-tree-preview&amp;treeId=...</code>
      </p>
    </div>
  );
}

root.render(<StrictMode>{renderTool()}</StrictMode>);
